import React, { useEffect, useState, useCallback } from 'react';
import { apiCall } from '../utils/api';

interface Gig {
  id: number;
  name: string;
  status: 'upcoming' | 'active' | 'ended';
  started_at: string;
  ended_at: string | null;
  scheduled_date: string | null;
  venue: string | null;
  total_songs_played: string;
  unique_performers: string;
  queue_count: string;
}

interface GigSong {
  id: number;
  title: string;
  artist: string;
  user_name: string;
  status: string;
  played_at: string;
  completed_at: string;
  average_rating: string;
  vote_count: string;
}

interface GigDetails extends Gig {
  songs: GigSong[];
}

const GigHistoryPage: React.FC = () => {
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [selectedGig, setSelectedGig] = useState<GigDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGigs = useCallback(async () => {
    try {
      const res = await apiCall('/api/songs/gigs');
      if (!res.ok) throw new Error('Failed to fetch gigs');
      const data = await res.json();
      setGigs(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGigs();
  }, [fetchGigs]);

  const handleGigClick = async (gigId: number) => {
    setLoadingDetails(true);
    try {
      const res = await apiCall(`/api/songs/gigs/${gigId}`);
      if (!res.ok) throw new Error('Failed to fetch gig details');
      const data = await res.json();
      setSelectedGig(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startStr: string, endStr: string | null) => {
    if (!endStr) return 'In progress';
    const start = new Date(startStr);
    const end = new Date(endStr);
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#f5deb3' }}>
        <p>Loading gig history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'red' }}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ color: '#f5deb3' }}>Gig History</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24, marginTop: 24 }}>
        {/* Gig List */}
        <div style={{
          background: 'rgba(40,25,15,0.95)',
          borderRadius: 8,
          padding: 16,
          border: '1px solid #5a3a1a'
        }}>
          <h2 style={{ marginTop: 0, color: '#f5deb3' }}>Past Gigs</h2>
          {gigs.length === 0 ? (
            <p style={{ color: '#806040' }}>No gigs recorded yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gigs.map(gig => (
                <div
                  key={gig.id}
                  onClick={() => handleGigClick(gig.id)}
                  style={{
                    padding: 16,
                    background: selectedGig?.id === gig.id
                      ? 'rgba(200,133,15,0.1)'
                      : 'rgba(40,25,15,0.95)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: selectedGig?.id === gig.id
                      ? '2px solid #c8850f'
                      : '1px solid #5a3a1a'
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#f5deb3' }}>{gig.name}</div>
                  <div style={{ fontSize: 14, color: '#a08060' }}>
                    {gig.status === 'upcoming' && gig.scheduled_date
                      ? new Date(gig.scheduled_date).toLocaleDateString()
                      : gig.started_at
                        ? new Date(gig.started_at).toLocaleDateString()
                        : 'No date'}
                    {gig.venue && <span> &bull; {gig.venue}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, flexWrap: 'wrap' }}>
                    <span style={{
                      background: gig.status === 'active' ? '#2ecc71' : gig.status === 'upcoming' ? '#c8850f' : '#a08060',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: 4
                    }}>
                      {gig.status === 'upcoming' ? 'Upcoming' : gig.status === 'active' ? 'Active' : 'Ended'}
                    </span>
                    {gig.status === 'upcoming' ? (
                      <span style={{ color: '#a08060' }}>{gig.queue_count || '0'} songs requested</span>
                    ) : (
                      <>
                        <span style={{ color: '#a08060' }}>{gig.total_songs_played} songs</span>
                        <span style={{ color: '#a08060' }}>{gig.unique_performers} performers</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gig Details */}
        <div style={{
          background: 'rgba(40,25,15,0.95)',
          borderRadius: 8,
          padding: 16,
          border: '1px solid #5a3a1a'
        }}>
          {loadingDetails ? (
            <p style={{ color: '#f5deb3' }}>Loading gig details...</p>
          ) : selectedGig ? (
            <div>
              <h2 style={{ marginTop: 0, color: '#f5deb3' }}>{selectedGig.name}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                <div style={{
                  background: 'rgba(40,25,15,0.95)',
                  padding: 16,
                  borderRadius: 8,
                  textAlign: 'center',
                  border: '1px solid #5a3a1a'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#ffd700' }}>
                    {selectedGig.songs.length}
                  </div>
                  <div style={{ fontSize: 12, color: '#a08060' }}>Songs Played</div>
                </div>
                <div style={{
                  background: 'rgba(40,25,15,0.95)',
                  padding: 16,
                  borderRadius: 8,
                  textAlign: 'center',
                  border: '1px solid #5a3a1a'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#ffd700' }}>
                    {new Set(selectedGig.songs.map(s => s.user_name)).size}
                  </div>
                  <div style={{ fontSize: 12, color: '#a08060' }}>Unique Performers</div>
                </div>
                <div style={{
                  background: 'rgba(40,25,15,0.95)',
                  padding: 16,
                  borderRadius: 8,
                  textAlign: 'center',
                  border: '1px solid #5a3a1a'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#ffd700' }}>
                    {selectedGig.started_at
                      ? formatDuration(selectedGig.started_at, selectedGig.ended_at)
                      : 'Upcoming'}
                  </div>
                  <div style={{ fontSize: 12, color: '#a08060' }}>Duration</div>
                </div>
              </div>

              {selectedGig.venue && (
                <div style={{ marginBottom: 16, color: '#f5deb3' }}>
                  <strong>Venue:</strong> {selectedGig.venue}
                </div>
              )}
              {selectedGig.scheduled_date && (
                <div style={{ marginBottom: 16, color: '#f5deb3' }}>
                  <strong>Scheduled:</strong> {formatDate(selectedGig.scheduled_date)}
                </div>
              )}
              {selectedGig.started_at && (
                <div style={{ marginBottom: 16, color: '#f5deb3' }}>
                  <strong>Started:</strong> {formatDate(selectedGig.started_at)}
                </div>
              )}
              {selectedGig.ended_at && (
                <div style={{ marginBottom: 16, color: '#f5deb3' }}>
                  <strong>Ended:</strong> {formatDate(selectedGig.ended_at)}
                </div>
              )}

              <h3 style={{ color: '#f5deb3' }}>Setlist</h3>
              {selectedGig.songs.length === 0 ? (
                <p style={{ color: '#806040' }}>No songs played during this gig</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #5a3a1a', color: '#a08060' }}>#</th>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #5a3a1a', color: '#a08060' }}>Song</th>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #5a3a1a', color: '#a08060' }}>Artist</th>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #5a3a1a', color: '#a08060' }}>Performer</th>
                      <th style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #5a3a1a', color: '#a08060' }}>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGig.songs.map((song, index) => (
                      <tr key={song.id} style={{ borderBottom: '1px solid rgba(90,58,26,0.5)' }}>
                        <td style={{ padding: 8, color: '#a08060' }}>{index + 1}</td>
                        <td style={{ padding: 8, color: '#f5deb3' }}>{song.title}</td>
                        <td style={{ padding: 8, color: '#a08060' }}>{song.artist}</td>
                        <td style={{ padding: 8, color: '#f5deb3' }}>{song.user_name}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>
                          {parseInt(song.vote_count) > 0 ? (
                            <span>
                              <span style={{ color: '#ffd700' }}>
                                {'★'.repeat(Math.round(parseFloat(song.average_rating)))}
                              </span>
                              <span style={{ color: '#806040' }}>
                                {'☆'.repeat(5 - Math.round(parseFloat(song.average_rating)))}
                              </span>
                              <span style={{ fontSize: 12, color: '#806040', marginLeft: 4 }}>
                                ({song.vote_count})
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: '#806040' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#806040', padding: 48 }}>
              Select a gig to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GigHistoryPage;
